<template>
	<section
		class="settings-section ehr-configuration-section new-settings__section"
		aria-labelledby="ehr-configuration-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<h1
						id="ehr-configuration-section-title"
						class="new-settings__section-title"
					>
						{{ strings.title }}
					</h1>
					<p class="new-settings__section-subtitle">
						{{ strings.subtitle }}
					</p>
				</div>
			</div>
		</header>

		<div class="settings-section__content">
			<engage-loader v-if="isEhrSectionBusy" overlay />

			<form
				v-show="!isEhrSectionBusy"
				class="ehr-configuration-section__form"
				@submit.prevent="onConfigSaveClick"
			>
				<div class="ehr-configuration-section__config-group">
					<div
						class="settings-section__card ehr-configuration-section__card"
					>
						<div
							class="settings-section__card-header ehr-configuration-section__card-header"
						>
							<h2 class="settings-section__card-title">
								{{ strings.configCardTitle }}
							</h2>
							<div
								v-if="isEhrSectionContentReady && partner"
								class="settings-section__card-actions"
							>
								<button
									v-if="!isEditingConfig"
									type="button"
									class="btn-pill btn-pill--sm"
									@click="startConfigEditing"
								>
									{{ common.edit }}
								</button>
								<div v-else class="btn-pill-group">
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										@click="cancelConfigEditing"
									>
										{{ common.cancel }}
									</button>
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										:disabled="isConfigSaveDisabled"
										@click="onConfigSaveClick"
									>
										{{ common.save }}
									</button>
								</div>
							</div>
						</div>
						<div
							class="settings-section__card-body ehr-configuration-section__card-body"
						>
							<div class="settings-section__field">
								<label class="settings-section__label">{{
									strings.fields.provider
								}}</label>
								<div
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ ehrProviderName }}
								</div>
							</div>

							<div
								class="settings-section__field-row settings-section__field-row--thirds"
							>
								<div class="settings-section__field">
									<label
										for="ehr-username"
										class="settings-section__label"
									>
										{{ strings.fields.username }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="ehr-username"
											v-model="form.ehr_config.username"
											type="text"
											class="settings-section__input settings-section__input--clearable"
											:placeholder="placeholders.username"
											:readonly="!isEditingConfig"
										/>
										<button
											v-if="
												isEditingConfig &&
													form.ehr_config.username
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="aria.clearUsername"
											@click="clearEhrField('username')"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>

								<div class="settings-section__field">
									<label
										for="ehr-password"
										class="settings-section__label"
									>
										{{ strings.fields.password }}
									</label>
									<div
										class="settings-section__passcode-wrap"
									>
										<input
											id="ehr-password"
											v-model="form.ehr_config.password"
											autocomplete="new-password"
											:type="
												showPasscode
													? 'text'
													: 'password'
											"
											class="settings-section__input"
											:placeholder="placeholders.password"
											:readonly="!isEditingConfig"
										/>
										<button
											v-if="form.ehr_config.password"
											type="button"
											class="settings-section__passcode-toggle"
											:data-tooltip="
												showPasscode
													? common.hidePassword
													: common.showPassword
											"
											@click="
												showPasscode = !showPasscode
											"
										>
											<font-awesome-icon
												:icon="[
													'fas',
													showPasscode
														? 'eye-slash'
														: 'eye',
												]"
											/>
										</button>
									</div>
								</div>

								<div class="settings-section__field">
									<label
										for="ehr-destination-id"
										class="settings-section__label"
									>
										{{ strings.fields.destinationId }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="ehr-destination-id"
											v-model="
												form.ehr_config.destination_id
											"
											type="text"
											class="settings-section__input settings-section__input--clearable"
											:placeholder="
												placeholders.destinationId
											"
											:readonly="!isEditingConfig"
										/>
										<button
											v-if="
												isEditingConfig &&
													form.ehr_config
														.destination_id
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearDestinationId
											"
											@click="
												clearEhrField('destination_id')
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
							</div>

							<div class="settings-section__field-row">
								<div class="settings-section__field">
									<label
										for="ehr-application-id"
										class="settings-section__label"
									>
										{{ strings.fields.applicationId }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="ehr-application-id"
											v-model="
												form.ehr_config
													.service_application_id
											"
											type="text"
											class="settings-section__input settings-section__input--clearable"
											:placeholder="
												placeholders.applicationId
											"
											:readonly="!isEditingConfig"
										/>
										<button
											v-if="
												isEditingConfig &&
													form.ehr_config
														.service_application_id
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearApplicationId
											"
											@click="
												clearEhrField(
													'service_application_id'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>

								<div class="settings-section__field">
									<label
										for="ehr-facility-id"
										class="settings-section__label"
									>
										{{ strings.fields.facilityId }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="ehr-facility-id"
											v-model="
												form.ehr_config
													.service_facility_id
											"
											type="text"
											class="settings-section__input settings-section__input--clearable"
											:placeholder="
												placeholders.facilityId
											"
											:readonly="!isEditingConfig"
										/>
										<button
											v-if="
												isEditingConfig &&
													form.ehr_config
														.service_facility_id
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="aria.clearFacilityId"
											@click="
												clearEhrField(
													'service_facility_id'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					v-if="showNoteTypesCard"
					class="settings-section__card-wrap ehr-configuration-section__note-types-wrap"
				>
					<div
						class="settings-section__card ehr-configuration-section__card ehr-configuration-section__card--note-types"
					>
						<div
							class="settings-section__card-header ehr-configuration-section__card-header"
						>
							<h2 class="settings-section__card-title">
								{{ strings.noteTypesCardTitle }}
							</h2>
							<div
								v-if="shouldShowNoteTypes"
								class="settings-section__card-actions"
							>
								<button
									v-if="!isEditingNoteTypes"
									type="button"
									class="btn-pill btn-pill--sm"
									@click="startNoteTypesEditing"
								>
									{{ common.edit }}
								</button>
								<div v-else class="btn-pill-group">
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										@click="cancelNoteTypesEditing"
									>
										{{ common.cancel }}
									</button>

									<button
										type="button"
										class="btn-pill btn-pill--sm"
										:disabled="isNoteTypesSaveDisabled"
										@click="onNoteTypesSaveClick"
									>
										{{ common.save }}
									</button>
								</div>
							</div>
						</div>
						<div
							class="settings-section__card-body"
							:class="{
								'ehr-configuration-section__card-body--flush': shouldShowNoteTypes,
							}"
						>
							<engage-loader v-if="isFetchingNoteTypes" inline />
							<p
								v-else-if="isNoteTypesFetchError"
								class="ehr-configuration-section__note-empty"
							>
								{{ strings.noteTypes.loadError }}
							</p>
							<p
								v-else-if="!shouldShowNoteTypes"
								class="ehr-configuration-section__note-empty"
							>
								{{ strings.noteTypes.empty }}
							</p>
							<div
								v-else
								class="ehr-configuration-section__note-panel"
							>
								<div
									class="ehr-configuration-section__note-list"
								>
									<div
										v-for="note in sortedNoteTypesWithSavedSelectedFirst"
										:key="note.id"
										class="ehr-configuration-section__note-item"
										@click="onClickNoteType(note)"
									>
										<label
											class="ehr-configuration-section__note-checkbox"
											:for="`ehr-note-${note.id}`"
										>
											<input
												:id="`ehr-note-${note.id}`"
												v-model="selectedValues"
												type="checkbox"
												:value="note.id"
												:disabled="!isEditingNoteTypes"
												@change="
													onNoteTypeSelected(note)
												"
											/>
											<span
												class="ehr-configuration-section__note-checkbox-ui"
											/>
										</label>
										<span
											class="ehr-configuration-section__note-label"
										>
											{{ note.title }}
										</span>
									</div>
								</div>
								<div
									class="ehr-configuration-section__note-preview"
								>
									<div
										v-if="
											currentNoteTitle ||
												shouldShowNoteTypesStaticBox
										"
										class="ehr-configuration-section__note-preview-title"
									>
										{{
											currentNoteTitle ||
												firstNoteTitleSelected
										}}
									</div>
									<div
										v-if="
											shouldShowNoteTypesStaticBox ||
												currentNoteTitle
										"
										class="ehr-configuration-section__note-preview-body"
									>
										<p
											class="ehr-configuration-section__note-preview-template"
										>
											<span
												class="ehr-configuration-section__preview-label"
												>{{
													strings.notePreview
														.callReceived
												}}</span
											>
											%call-ended%
											<span
												class="ehr-configuration-section__preview-separator"
												aria-hidden="true"
											>
												---
											</span>
											<span
												class="ehr-configuration-section__preview-label"
												>{{
													strings.notePreview.message
												}}</span
											>
											"%message%"
											<span
												class="ehr-configuration-section__preview-separator"
												aria-hidden="true"
											>
												---
											</span>
											<span
												class="ehr-configuration-section__preview-label"
												>{{
													strings.notePreview.notes
												}}</span
											>
											%notes%
										</p>
									</div>
								</div>
							</div>
							<p
								v-if="
									shouldShowNoteTypes &&
										(showNoteWarning ||
											noteTypesErrorMessage)
								"
								class="settings-section__error ehr-configuration-section__note-warning"
							>
								{{
									noteTypesErrorMessage ||
										validation.noteTypeRequired
								}}
							</p>
						</div>
					</div>
				</div>
			</form>
		</div>
	</section>
</template>

<script>
import _get from 'lodash/get'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import EngageLoader from '@/components/EngageLoader.vue'
import {
	EHR_CONFIGURATION_STRINGS,
	SETTINGS_ARIA,
	SETTINGS_COMMON,
	SETTINGS_PLACEHOLDERS,
	SETTINGS_VALIDATION,
} from '@/config/settingsConstants'

let cachedEhrNoteTypesPartnerId = null

/**
 * Clear cached EHR note types (e.g. when settings partner changes).
 *
 * @return {void}
 */
export function invalidateEhrNoteTypesCache() {
	cachedEhrNoteTypesPartnerId = null
}

/**
 * Empty partner settings form shape.
 *
 * @return {Object}
 */
function getSettingsFormObject() {
	return {
		name: '',
		wma_display_name: '',
		wma_web_link: '',
		office_phone_number: '',
		office_fax_number: '',
		ob_caller_id_number: '',
		address_1: '',
		address_2: '',
		city: '',
		state_province: '',
		country: '',
		postal_code: '',
		fallback_email: '',
		fallback_phone_number: '',
		timezone: '',
		rsm_fallback_email: '',
		passcode: '',
		ehr_config: {
			username: '',
			password: '',
			destination_id: '',
			service_application_id: '',
			service_facility_id: '',
			note_types: '',
			metadata: '',
		},
	}
}

/**
 * EHR configuration settings — credentials and coordination note types.
 */
export default {
	components: {
		EngageLoader,
	},

	inject: {
		ensureSettingsPartnerLoaded: { default: null },
	},

	computed: {
		fallbackEmailTags: {
			get() {
				const emails = this.form.rsm_fallback_email
				if (emails && emails.trim()) {
					return emails.split(/,|;|\s+/).filter(email => email.trim())
				}
				return []
			},
			set(newEmails) {
				this.form.rsm_fallback_email = newEmails.join(', ')
			},
		},

		isSaveDisabled() {
			if (!this.partner) {
				return true
			}
			if (!this.lastSavedSnapshot) {
				return true
			}
			return !this.hasUnsavedChanges
		},

		hasUnsavedChanges() {
			if (!this.lastSavedSnapshot) {
				return false
			}
			return this.lastSavedSnapshot !== this.getCurrentSnapshot()
		},

		partnerId() {
			return parseInt(this.$route.params.partner, 10)
		},

		sortedNoteTypes() {
			return this.noteTypes
				.slice()
				.sort((a, b) => a.title.localeCompare(b.title))
		},

		sortedNoteTypesWithSavedSelectedFirst() {
			const selectedNoteTypes = this.sortedNoteTypes.filter(note =>
				this.savedSelectedValues.includes(note.id)
			)
			const remainingNoteTypes = this.sortedNoteTypes.filter(
				note => !this.savedSelectedValues.includes(note.id)
			)
			return [...selectedNoteTypes, ...remainingNoteTypes]
		},

		shouldShowNoteTypesStaticBox() {
			return this.selectedValues.length > 0
		},

		isEhrFormReady() {
			return Boolean(this.form?.ehr_config && this.partner)
		},

		ehrProviderName() {
			return _get(this.partner, 'ehr_settings.provider.name', '')
		},

		firstNoteTitleSelected() {
			try {
				const noteTypes = JSON.parse(
					_get(this.partner, 'ehr_settings.note_types', '[]')
				)
				noteTypes.sort((a, b) => {
					const nameA = a.note_name.toUpperCase()
					const nameB = b.note_name.toUpperCase()
					const isNumberA = /^\d/.test(nameA)
					const isNumberB = /^\d/.test(nameB)
					if (isNumberA && !isNumberB) {
						return -1
					}
					if (!isNumberA && isNumberB) {
						return 1
					}
					return nameA.localeCompare(nameB)
				})
				return noteTypes.length ? noteTypes[0].note_name : ''
			} catch (e) {
				return ''
			}
		},

		shouldShowNoteTypes() {
			return (
				this.noteTypes &&
				this.noteTypes.length > 0 &&
				!this.isNoteTypesFetchError
			)
		},

		showNoteTypesCard() {
			return this.isEhrConfiguredSwitch()
		},

		isConfigSaveDisabled() {
			if (!this.partner || !this.lastSavedSnapshot) {
				return true
			}

			const saved = JSON.parse(this.lastSavedSnapshot)
			const current = JSON.parse(this.getCurrentSnapshot())

			return (
				JSON.stringify(saved.form?.ehr_config) ===
				JSON.stringify(current.form?.ehr_config)
			)
		},

		isNoteTypesSaveDisabled() {
			if (!this.lastSavedSnapshot) {
				return true
			}

			const saved = JSON.parse(this.lastSavedSnapshot)
			const savedIds = [...(saved.selectedValues || [])].sort()
			const currentIds = [...this.selectedValues].sort()

			return JSON.stringify(savedIds) === JSON.stringify(currentIds)
		},

		isEhrSectionBusy() {
			return this.isPartnerFormLoading || this.isEhrSectionLoading
		},

		isEhrSectionContentReady() {
			return !this.isEhrSectionBusy && this.isEhrFormReady
		},

		...mapGetters({
			partner: 'partners/active',
		}),
	},

	methods: {
		fields() {
			return Object.keys(this.form)
		},

		fillForm() {
			if (!this.partner) {
				return this.resetFormFields()
			}

			this.fields().forEach(key => {
				if (key === 'ehr_config') {
					return
				}

				if (
					this.partner[key] !== null &&
					typeof this.partner[key] !== 'undefined'
				) {
					if (
						[
							'office_phone_number',
							'office_fax_number',
							'fallback_phone_number',
							'ob_caller_id_number',
						].includes(key)
					) {
						this.form[key] = this.partner[key].replace(/^\+1/, '')
					} else {
						this.form[key] = this.partner[key]
					}
				}
			})

			if (this.partner.ehr_settings) {
				this.form.ehr_config.username =
					this.partner.ehr_settings.username || ''
				this.form.ehr_config.password =
					this.partner.ehr_settings.password || ''
				this.form.ehr_config.destination_id =
					this.partner.ehr_settings.destination_id || ''
				this.form.ehr_config.service_application_id =
					this.partner.ehr_settings.service_application_id || ''
				this.form.ehr_config.service_facility_id =
					this.partner.ehr_settings.service_facility_id || ''
				this.form.ehr_config.metadata =
					this.partner.ehr_settings.metadata || {}
			}

			this.officeNumberErrorMessage = ''
			this.passcodeErrorMessage = ''
			this.fallbackNumberErrorMessage = ''
			this.showPasscode = false
			this.wasCleared = false
			this.form.rsm_fallback_email =
				this.partner.rsm_fallback_email ||
				this.partner.fallback_email ||
				''
		},

		resetFormFields() {
			this.form = getSettingsFormObject()
			this.ensureEhrConfigForm()
		},

		findDuplicates(emails) {
			const duplicates = []
			const emailMap = new Map()

			emails.forEach(email => {
				const normalizedEmail = email.toLowerCase()
				if (emailMap.has(normalizedEmail)) {
					duplicates.push(email)
				} else {
					emailMap.set(normalizedEmail, true)
				}
			})
			return duplicates
		},

		async loadPartnerForm({ force = false } = {}) {
			this.ensureEhrConfigForm()

			try {
				this.isPartnerFormLoading = true

				if (this.ensureSettingsPartnerLoaded) {
					await this.ensureSettingsPartnerLoaded(force)
					this.fillForm()
					await this.afterPartnerFormReady()
					return
				}

				await this.getPartnerData()
			} finally {
				this.isPartnerFormLoading = false
			}
		},

		async refreshSettingsPartner() {
			if (this.ensureSettingsPartnerLoaded) {
				await this.ensureSettingsPartnerLoaded(true)
				this.fillForm()
				await this.afterPartnerFormReady()
				return
			}

			await this.getPartnerData()
		},

		isSequential(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					parseInt(passcode[i], 10) + 1 ===
						parseInt(passcode[i + 1], 10) &&
					parseInt(passcode[i + 1], 10) + 1 ===
						parseInt(passcode[i + 2], 10)
				) {
					return true
				}
			}
			return false
		},

		isRepeating(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					passcode[i] === passcode[i + 1] &&
					passcode[i + 2] === passcode[i]
				) {
					return true
				}
			}
			return false
		},

		validatePasscode() {
			const passcode = this.form.passcode

			if (!passcode) {
				this.passcodeErrorMessage = ''
				return true
			}

			if (!/^\d+$/.test(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeDigitsOnly
				return false
			}

			if (passcode.length < 6) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeMinLength
				return false
			}

			if (this.isSequential(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoSequential
				return false
			}

			if (this.isRepeating(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoRepeating
				return false
			}

			this.passcodeErrorMessage = ''
			return true
		},

		validateOfficePhoneNumber() {
			const sanitizedPhoneNumber = this.form.office_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.officeNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilio
				return false
			}

			this.officeNumberErrorMessage = ''
			return true
		},

		validateFallbackPhoneNumber() {
			const sanitizedPhoneNumber = this.form.fallback_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			const sanitizedOfficePhone = this.form.office_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.fallbackNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			if (sanitizedPhoneNumber === sanitizedOfficePhone) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			this.fallbackNumberErrorMessage = ''
			return true
		},

		async getPartnerData() {
			if (!this.partner?.id) {
				return
			}
			try {
				await this.getPartner(this.partner.id)
			} catch (e) {
				this.$alert.error('default.error')
			} finally {
				this.fillForm()
				this.takeSavedSnapshot()
			}
		},

		getCurrentSnapshot() {
			const normalizedEmails = (this.form.rsm_fallback_email || '')
				.split(',')
				.map(e => e.trim())
				.filter(Boolean)
				.join(', ')

			return JSON.stringify({
				form: {
					...this.form,
					rsm_fallback_email: normalizedEmails,
				},
				fallbackEmailTags: [...this.fallbackEmailTags],
				selectedValues: [...this.selectedValues].sort(),
			})
		},

		takeSavedSnapshot() {
			this.lastSavedSnapshot = this.getCurrentSnapshot()
		},

		restoreFromSnapshot() {
			this.restoreConfigFromSnapshot()
			this.restoreNoteTypesFromSnapshot()
		},

		isEhrConfiguredSwitch() {
			return Boolean(this.partner?.ehr_settings)
		},

		ensureEhrConfigForm() {
			if (!this.form || typeof this.form !== 'object') {
				this.form = getSettingsFormObject()
			}

			const defaults = getSettingsFormObject().ehr_config

			if (
				!this.form.ehr_config ||
				typeof this.form.ehr_config !== 'object'
			) {
				this.$set(this.form, 'ehr_config', { ...defaults })
			}
		},

		onNoteTypeSelected(note) {
			this.currentNoteTitle = note.title
			if (this.areAtLeastOneNoteSelected()) {
				this.showNoteWarning = false
			}
		},

		onClickNoteType(note) {
			this.currentNoteTitle = note.title
		},

		areAtLeastOneNoteSelected() {
			return this.selectedValues.length > 0
		},

		clearCurrentNoteTitle() {
			this.currentNoteTitle = null
		},

		clearEhrField(field) {
			this.form.ehr_config[field] = ''
		},

		fillEhrFormExtras() {
			this.ensureEhrConfigForm()

			if (!this.partner?.ehr_settings) {
				this.selectedValues = []
				this.savedSelectedValues = []
				this.currentNoteTitle = ''
				return
			}

			const ehr = this.partner.ehr_settings
			this.form.ehr_config.username = ehr.username || ''
			this.form.ehr_config.password = ehr.password || ''
			this.form.ehr_config.destination_id = ehr.destination_id || ''
			this.form.ehr_config.service_application_id =
				ehr.service_application_id || ''
			this.form.ehr_config.service_facility_id =
				ehr.service_facility_id || ''
			this.form.ehr_config.metadata = ehr.metadata || {}

			if (ehr.note_types && !this.isNoteTypesFetchError) {
				try {
					const partnerNotes = JSON.parse(ehr.note_types).map(
						x => x.note_id
					)
					this.selectedValues = [...partnerNotes]
					this.savedSelectedValues = [...partnerNotes]
				} catch (error) {
					console.error('Error parsing note types:', error)
					this.selectedValues = []
					this.savedSelectedValues = []
				}
			} else {
				this.selectedValues = []
				this.savedSelectedValues = []
			}

			this.showNoteWarning = false
			this.currentNoteTitle = ''
		},

		validateNoteTypes() {
			if (
				this.selectedValues.length === 0 &&
				this.noteTypes &&
				this.noteTypes.length > 0
			) {
				this.noteTypesErrorMessage =
					SETTINGS_VALIDATION.noteTypeRequired
				return false
			}
			this.noteTypesErrorMessage = ''
			return true
		},

		async fetchCoordinationNoteTypes() {
			const partnerId = this.partnerId

			if (!partnerId) {
				this.isNoteTypesFetchError = true
				return false
			}

			const apiUrl = `${process.env.VUE_APP_API_URL}/api/v4/ehr/note-types?partner_id=${partnerId}`

			try {
				const response = await fetch(apiUrl, {
					headers: {
						Authorization: `Bearer ${this.$store.state.auth.session.access_token}`,
					},
				})

				if (!response.ok) {
					if (response.status === 401 || response.status === 403) {
						this.noteTypes = []
						this.isNoteTypesFetchError = true
						return false
					}

					throw new Error('Failed to fetch coordination note types')
				}

				const data = await response.json()

				if (Array.isArray(data.note_types)) {
					this.noteTypes = data.note_types
					this.isNoteTypesFetchError = false
					return this.noteTypes.length > 0
				}

				this.noteTypes = []
				this.isNoteTypesFetchError = true
				return false
			} catch (error) {
				this.noteTypes = []
				this.isNoteTypesFetchError = true
				return false
			}
		},

		async ensureNoteTypesLoaded(force = false) {
			if (!this.isEhrConfiguredSwitch()) {
				return
			}

			const partnerId = this.partner?.id

			if (!partnerId) {
				return
			}

			const hasCachedTypes =
				!force &&
				cachedEhrNoteTypesPartnerId === partnerId &&
				this.noteTypes.length > 0 &&
				!this.isNoteTypesFetchError

			if (hasCachedTypes) {
				return
			}

			this.isFetchingNoteTypes = true

			try {
				const loaded = await this.fetchCoordinationNoteTypes()

				if (loaded) {
					cachedEhrNoteTypesPartnerId = partnerId
					this.fillEhrFormExtras()
				} else {
					cachedEhrNoteTypesPartnerId = null
				}
			} finally {
				this.isFetchingNoteTypes = false
			}
		},

		getNoteTypesPayload() {
			const fromPasswordVal = this.form.ehr_config.password
			const formDestId = this.form.ehr_config.destination_id
			const formUserName = this.form.ehr_config.username

			const ehrSettingsVal = this.partner?.ehr_settings || {}
			const apiValPass = ehrSettingsVal.password
			const apiValDest = ehrSettingsVal.destination_id
			const apiValUserName = ehrSettingsVal.username

			if (
				fromPasswordVal !== apiValPass ||
				formDestId !== apiValDest ||
				formUserName !== apiValUserName
			) {
				return []
			}

			return this.selectedValues
				.map(noteId => {
					const note = this.noteTypes.find(n => n.id === noteId)
					if (note) {
						return {
							note_id: note.id,
							note_name: note.title,
						}
					}
					return null
				})
				.filter(note => note !== null)
		},

		restoreConfigFromSnapshot() {
			if (!this.lastSavedSnapshot) {
				return
			}

			const snapshot = JSON.parse(this.lastSavedSnapshot)

			if (snapshot.form?.ehr_config) {
				this.form.ehr_config = {
					...this.form.ehr_config,
					...snapshot.form.ehr_config,
				}
			}

			this.showPasscode = false
		},

		restoreNoteTypesFromSnapshot() {
			if (!this.lastSavedSnapshot) {
				return
			}

			const snapshot = JSON.parse(this.lastSavedSnapshot)

			if (snapshot.selectedValues) {
				this.selectedValues = [...snapshot.selectedValues]
				this.savedSelectedValues = [...snapshot.selectedValues]
			}

			this.showNoteWarning = false
			this.noteTypesErrorMessage = ''
		},

		async saveEhrSettings({ requireChanges = true } = {}) {
			if (requireChanges && this.isSaveDisabled) {
				return false
			}

			if (
				!this.areAtLeastOneNoteSelected() &&
				!this.isNoteTypesFetchError
			) {
				this.showNoteWarning = true
				this.$alert.error(SETTINGS_VALIDATION.noteTypeRequired)
				return false
			}

			this.showNoteWarning = false

			if (!this.validateNoteTypes()) {
				return false
			}

			if (!this.validatePasscode()) {
				this.$alert.error(this.passcodeErrorMessage)
				return false
			}

			if (!this.validateOfficePhoneNumber()) {
				this.$alert.error(this.officeNumberErrorMessage)
				return false
			}

			if (!this.validateFallbackPhoneNumber()) {
				this.$alert.error(this.fallbackNumberErrorMessage)
				return false
			}

			if (!this.form.rsm_fallback_email && this.form.fallback_email) {
				this.form.rsm_fallback_email = this.form.fallback_email
			}

			if (this.form.rsm_fallback_email) {
				const emails = this.form.rsm_fallback_email
					.split(/,|;|\n|\s/)
					.map(email => email.trim())
					.filter(email => email)
				const duplicates = this.findDuplicates(emails)
				if (duplicates.length > 0) {
					this.$alert.error(
						SETTINGS_VALIDATION.duplicateEmails(duplicates)
					)
					return false
				}
				this.form.rsm_fallback_email = emails.join(', ')
			}

			const noteTypesPayload = this.getNoteTypesPayload()

			const payload = {
				partner: {
					id: this.partner.id,
					name: this.form.name,
					wma_display_name: this.form.wma_display_name,
					wma_web_link: this.form.wma_web_link,
					office_phone_number: this.form.office_phone_number,
					office_fax_number: this.form.office_fax_number,
					address_1: this.form.address_1,
					address_2: this.form.address_2,
					city: this.form.city,
					state_province: this.form.state_province,
					postal_code: this.form.postal_code,
					rsm_fallback_email: this.form.rsm_fallback_email,
					country: this.form.country,
					fallback_email: this.form.fallback_email,
					fallback_phone_number: this.form.fallback_phone_number,
					timezone: this.form.timezone,
					passcode: this.form.passcode,
				},
				ehr_config: {
					username: this.form.ehr_config.username,
					password: this.form.ehr_config.password,
					destination_id: this.form.ehr_config.destination_id,
					service_application_id: this.form.ehr_config
						.service_application_id,
					service_facility_id: this.form.ehr_config
						.service_facility_id,
					note_types: noteTypesPayload,
					metadata: this.form.ehr_config.metadata,
				},
			}

			try {
				await this.update({
					id: this.partner.id,
					fromSettings: true,
					payload,
				})
				this.$alert.success('partners.success')
				this.takeSavedSnapshot()
				this.showPasscode = false
				this.wasCleared = false

				if (this.isEhrConfiguredSwitch()) {
					invalidateEhrNoteTypesCache()
					await this.ensureNoteTypesLoaded(true)
					this.savedSelectedValues = [...this.selectedValues]
				}
				return true
			} catch (e) {
				this.$alert.error('partners.error')
				return false
			}
		},

		afterPartnerFormReady() {
			this.fillEhrFormExtras()
			this.takeSavedSnapshot()
		},

		ensureEhrEditingState() {
			if (!('isEditingConfig' in this)) {
				this.$set(this, 'isEditingConfig', false)
			}
			if (!('isEditingNoteTypes' in this)) {
				this.$set(this, 'isEditingNoteTypes', false)
			}
		},

		startConfigEditing() {
			if (this.isEditingNoteTypes) {
				this.cancelNoteTypesEditing()
			}
			this.isEditingConfig = true
		},

		cancelConfigEditing() {
			this.restoreConfigFromSnapshot()
			this.isEditingConfig = false
		},

		async onConfigSaveClick() {
			const saved = await this.saveEhrSettings({
				requireChanges: !this.isConfigSaveDisabled,
			})
			if (saved) {
				this.isEditingConfig = false
			}
		},

		startNoteTypesEditing() {
			if (this.isEditingConfig) {
				this.cancelConfigEditing()
			}
			this.isEditingNoteTypes = true
			this.showNoteWarning = false
		},

		cancelNoteTypesEditing() {
			this.restoreNoteTypesFromSnapshot()
			this.isEditingNoteTypes = false
			this.clearCurrentNoteTitle()
		},

		async onNoteTypesSaveClick() {
			if (this.isNoteTypesSaveDisabled) {
				return
			}

			this.clearCurrentNoteTitle()
			const saved = await this.saveEhrSettings({
				requireChanges: false,
			})
			if (saved) {
				this.isEditingNoteTypes = false
			}
		},

		cancelAllEditing() {
			if (this.isEditingConfig) {
				this.cancelConfigEditing()
			}
			if (this.isEditingNoteTypes) {
				this.cancelNoteTypesEditing()
			}
		},

		resetEhrSectionForPartnerChange() {
			invalidateEhrNoteTypesCache()
			this.isEditingConfig = false
			this.isEditingNoteTypes = false
			this.noteTypes = []
			this.selectedValues = []
			this.savedSelectedValues = []
			this.isNoteTypesFetchError = false
			this.showNoteWarning = false
			this.noteTypesErrorMessage = ''
			this.currentNoteTitle = ''
		},

		async loadEhrSection({ force = false } = {}) {
			if (!this.isEhrSectionActive) {
				return
			}

			try {
				this.isEhrSectionLoading = true
				await this.loadPartnerForm({ force })
				await this.ensureNoteTypesLoaded(force)
			} catch (error) {
				this.isNoteTypesFetchError = true
			} finally {
				this.isEhrSectionLoading = false
			}
		},

		...mapActions({
			update: 'partners/update',
			getPartner: 'partners/find',
		}),
	},

	name: 'EhrConfigurationSection',

	watch: {
		'form.ehr_config.password'(newVal) {
			if (newVal != null && newVal.length === 0) {
				this.wasCleared = true
			}
		},

		partner: {
			immediate: true,
			handler(newPartner, oldPartner) {
				if (!newPartner) {
					return
				}
				if (!oldPartner || newPartner.id !== oldPartner.id) {
					this.fillForm()
					if (!this.ensureSettingsPartnerLoaded) {
						this.$nextTick(() => {
							if (!this.lastSavedSnapshot) {
								this.takeSavedSnapshot()
							}
						})
					}
				}
			},
		},

		async 'partner.id'(newId, oldId) {
			if (!newId || newId === oldId || !this.isEhrSectionActive) {
				return
			}

			if (oldId != null) {
				this.resetEhrSectionForPartnerChange()
			}

			await this.loadEhrSection({ force: oldId != null })
		},
	},

	async created() {
		this.form = getSettingsFormObject()
		this.timezones = moment.tz.zonesForCountry('us')
	},

	async mounted() {
		this.isEhrSectionActive = true
		this.ensureEhrEditingState()
		await this.loadEhrSection()
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.isEhrSectionActive = false
		this.cancelAllEditing()
	},

	async activated() {
		this.isEhrSectionActive = true
		this.ensureEhrEditingState()
		await this.loadEhrSection()
	},

	data() {
		return {
			common: SETTINGS_COMMON,
			strings: EHR_CONFIGURATION_STRINGS,
			placeholders: SETTINGS_PLACEHOLDERS,
			aria: SETTINGS_ARIA,
			validation: SETTINGS_VALIDATION,
			form: getSettingsFormObject(),
			isPartnerFormLoading: false,
			timezones: [],
			wasCleared: false,
			lastSavedSnapshot: null,
			showPasscode: false,
			passcodeErrorMessage: '',
			fallbackNumberErrorMessage: '',
			officeNumberErrorMessage: '',
			noteTypes: [],
			selectedValues: [],
			savedSelectedValues: [],
			currentNoteTitle: '',
			isNoteTypesFetchError: false,
			isFetchingNoteTypes: false,
			noteTypesErrorMessage: '',
			showNoteWarning: false,
			isEditingConfig: false,
			isEditingNoteTypes: false,
			isEhrSectionActive: false,
			isEhrSectionLoading: false,
			deferPartnerFormInit: true,
		}
	},
}
</script>
